import { useState, useEffect, useCallback, useMemo } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { AcademyinfoService } from '@/api/generated/services/AcademyinfoService'
import type { IndicatorMasterItem } from '@/api/generated/models/IndicatorMasterItem'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'

interface HiddenIndicatorModalProps {
  isOpen: boolean
  orgId: string
  onClose: () => void
  onSaved?: () => void
}

export default function HiddenIndicatorModal({
  isOpen,
  orgId,
  onClose,
  onSaved,
}: HiddenIndicatorModalProps) {
  const { showError, showSuccess } = useAlert()

  const [indicators, setIndicators] = useState<IndicatorMasterItem[]>([])
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())
  const [initialHiddenIds, setInitialHiddenIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const fetchIndicators = useCallback(async () => {
    if (!orgId) return
    setIsLoading(true)
    try {
      const response = await AcademyinfoService.appsAcademyinfoViewsListIndicators({
        orgId,
        includeHidden: true,
      })
      setIndicators(response)
      const hidden = new Set(response.filter(i => i.hidden === true).map(i => i.id))
      setHiddenIds(hidden)
      setInitialHiddenIds(new Set(hidden))
    } catch (error) {
      handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
    } finally {
      setIsLoading(false)
    }
  }, [orgId, showError])

  useEffect(() => {
    if (isOpen) {
      fetchIndicators()
    }
  }, [isOpen, fetchIndicators])

  const toggleHidden = (id: string) => {
    setHiddenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleShowAll = () => {
    setHiddenIds(new Set())
  }

  const isDirty = useMemo(() => {
    if (hiddenIds.size !== initialHiddenIds.size) return true
    for (const id of hiddenIds) {
      if (!initialHiddenIds.has(id)) return true
    }
    return false
  }, [hiddenIds, initialHiddenIds])

  const handleSave = async () => {
    if (!orgId) return
    setIsSaving(true)
    try {
      const response = await AcademyinfoService.appsAcademyinfoViewsUpdateHiddenIndicators({
        orgId,
        requestBody: {
          hidden_master_ids: Array.from(hiddenIds),
        },
      })
      const applied = new Set(response.hidden_master_ids || [])
      setHiddenIds(applied)
      setInitialHiddenIds(new Set(applied))
      showSuccess('공시항목 설정이 저장되었습니다.')
      onSaved?.()
      onClose()
    } catch (error) {
      handleApiError(error, showError, ErrorMessages.UPDATE_FAILED)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const visibleCount = indicators.length - hiddenIds.size

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        onClick={handleClose}
      />

      <div
        className="relative bg-white rounded shadow-xl flex flex-col"
        style={{
          width: '720px',
          maxWidth: '95vw',
          maxHeight: '85vh',
          border: `1px solid ${designTokens.colors.gray[1]}`,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
        >
          <div>
            <h2
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: 700,
                color: designTokens.colors.gray[6],
              }}
            >
              공시항목 관리
            </h2>
            <p
              className="mt-1"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '13px',
                color: designTokens.colors.gray[4],
              }}
            >
              학교 비교에서 보여줄 공시항목을 선택하세요. 체크 해제된 항목은 사용자에게 노출되지 않습니다.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity flex-shrink-0"
            style={{ color: designTokens.colors.gray[5] }}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Toolbar */}
        <div
          className="flex items-center justify-between px-6 py-3 flex-shrink-0"
          style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}`, backgroundColor: designTokens.colors.gray[0] }}
        >
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '13px',
              color: designTokens.colors.gray[5],
            }}
          >
            전체 {indicators.length}개 · 표시 {visibleCount}개 · 숨김 {hiddenIds.size}개
          </span>
          <button
            type="button"
            onClick={handleShowAll}
            disabled={isLoading || isSaving || hiddenIds.size === 0}
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '13px',
              fontWeight: 500,
              color: hiddenIds.size === 0 ? designTokens.colors.gray[3] : designTokens.colors.secondary.dark,
              background: 'none',
              border: 'none',
              cursor: hiddenIds.size === 0 ? 'not-allowed' : 'pointer',
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            모두 표시로 초기화
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
                공시항목을 불러오는 중...
              </span>
            </div>
          ) : indicators.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <span style={{ fontFamily: designTokens.typography.fontFamily.sans[0], fontSize: '14px', color: designTokens.colors.gray[4] }}>
                공시항목이 없습니다.
              </span>
            </div>
          ) : (
            <ul className="flex flex-col">
              {indicators.map(indicator => {
                const isHidden = hiddenIds.has(indicator.id)
                const isVisible = !isHidden
                return (
                  <li
                    key={indicator.id}
                    style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
                  >
                    <label
                      className="flex items-center gap-3 px-2 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ userSelect: 'none' }}
                    >
                      <input
                        type="checkbox"
                        checked={isVisible}
                        onChange={() => toggleHidden(indicator.id)}
                        disabled={isSaving}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: designTokens.colors.primary.DEFAULT,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          color: designTokens.colors.gray[4],
                          width: '40px',
                          flexShrink: 0,
                        }}
                      >
                        {indicator.code}
                      </span>
                      <span
                        className="flex-1"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: isVisible ? 500 : 400,
                          color: isVisible ? designTokens.colors.gray[6] : designTokens.colors.gray[4],
                          textDecoration: isHidden ? 'line-through' : 'none',
                        }}
                      >
                        {indicator.name}
                      </span>
                      {indicator.unit && (
                        <span
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '12px',
                            color: designTokens.colors.gray[4],
                            flexShrink: 0,
                          }}
                        >
                          {indicator.unit}
                        </span>
                      )}
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-6 py-4 flex-shrink-0"
          style={{ borderTop: `1px solid ${designTokens.colors.gray[1]}` }}
        >
          <Button
            size="medium"
            color="white"
            onClick={handleClose}
            disabled={isSaving}
          >
            취소
          </Button>
          <Button
            size="medium"
            color="dark"
            onClick={handleSave}
            disabled={isLoading || isSaving || !isDirty}
          >
            {isSaving ? '저장 중...' : '저장'}
          </Button>
        </div>
      </div>
    </div>
  )
}
